import { useEffect, useMemo, useState } from 'react';
import { BarChart3, FileSpreadsheet, Loader2 } from 'lucide-react';
import { reportService } from '../services/reportService';
import * as XLSX from 'xlsx-js-style';

type ReturnLoadRow = {
  city: string;
  count: number;
  ratio: number;
};

type ReturnLoadSummary = {
  subtotal: number;
  dailyAverage: number;
  activeDayCount: number;
  cityCount: number;
};

export function ReturnLoadPotentialReport() {
  const [rows, setRows] = useState<ReturnLoadRow[]>([]);
  const [summary, setSummary] = useState<ReturnLoadSummary>({ subtotal: 0, dailyAverage: 0, activeDayCount: 0, cityCount: 0 });
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today.slice(0, 8) + '01');
  const [endDate, setEndDate] = useState(today);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response: any = await reportService.getReturnLoadPotential({ startDate, endDate });
        setRows(Array.isArray(response?.data?.rows) ? response.data.rows : []);
        setSummary(response?.data?.summary || { subtotal: 0, dailyAverage: 0, activeDayCount: 0, cityCount: 0 });
      } catch (error) {
        console.error('Return load potential report load failed:', error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [startDate, endDate]);

  const ratioTotal = useMemo(() => rows.reduce((sum, row) => sum + Number(row.ratio || 0), 0), [rows]);

  const exportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(rows.map((row) => ({
      İl: row.city,
      Adet: row.count,
      'Toplam İçindeki Oran (%)': row.ratio,
      'Harici İller Alt Toplamı': summary.subtotal,
      'Günlük Ortalama': summary.dailyAverage,
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dönüş Yük Potansiyeli');
    XLSX.writeFile(workbook, `donus-yuk-potansiyeli-${startDate}-${endDate}.xlsx`);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dönüş Yükü Potansiyeli</h1>
          <p className="mt-1 text-sm text-gray-500">Workiom pickup ili kayıtlarından, depo illeri hariç şehir bazlı dönüş yükü potansiyeli.</p>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={exportExcel} disabled={loading || rows.length === 0} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            <FileSpreadsheet className="h-4 w-4" />
            Excele Aktar
          </button>
          {loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-500" /> : <BarChart3 className="h-6 w-6 text-slate-500" />}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Başlangıç Tarihi</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-violet-500" />
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Bitiş Tarihi</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-violet-500" />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-500">Harici İller Alt Toplam</div><div className="mt-2 text-2xl font-bold text-slate-900">{summary.subtotal.toLocaleString('tr-TR')}</div></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-500">Günlük Ortalama</div><div className="mt-2 text-2xl font-bold text-slate-900">{summary.dailyAverage.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</div></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-500">Aktif Gün</div><div className="mt-2 text-2xl font-bold text-slate-900">{summary.activeDayCount.toLocaleString('tr-TR')}</div></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4"><div className="text-xs text-slate-500">İl Sayısı</div><div className="mt-2 text-2xl font-bold text-slate-900">{summary.cityCount.toLocaleString('tr-TR')}</div></div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-slate-600">İl</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-600">Adet</th>
              <th className="px-4 py-3 text-right font-semibold text-slate-600">Toplam İçindeki Oran</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-500">Yükleniyor...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-500">Kayıt bulunamadı.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.city} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium text-slate-900">{row.city}</td>
                <td className="px-4 py-3 text-right text-slate-700">{Number(row.count || 0).toLocaleString('tr-TR')}</td>
                <td className="px-4 py-3 text-right text-slate-700">%{Number(row.ratio || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              </tr>
            ))}
          </tbody>
          {!loading && rows.length > 0 && (
            <tfoot className="bg-slate-50/80">
              <tr>
                <td className="px-4 py-3 font-semibold text-slate-900">Toplam</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900">{summary.subtotal.toLocaleString('tr-TR')}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900">%{ratioTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
